import { Plus } from 'lucide-react';
import { useMutation } from '@platform/api-client';
import { useAuth } from '@platform/auth-client';
import { Button, ChoiceChips, formatDate, formatMoney, type Column } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { useState } from 'react';
import { useCompanies } from '../companies.ts';
import { CompanyFilter } from '../CompanyFilter.tsx';
import { KeysetTable } from '../KeysetTable.tsx';
import { usePageContext } from '../usePageContext.ts';
import { ExpenseSheet } from './ExpenseSheet.tsx';
import {
  EXPENSE_VIEWS,
  FINANCE_WRITE_ROLE,
  useExpenses,
  type Expense,
  type ExpenseBody,
  type ExpenseView,
} from './finance.ts';
import { localDay } from '../report-period.ts';
import styles from './Finance.module.css';
import { FinanceFrame } from './FinanceFrame.tsx';
import type { FinanceSection } from './FinanceSections.tsx';

export type ExpensesPageProps = {
  api: TypedClient;
  view: ExpenseView;
  company: string | null;
  onViewChange: (view: ExpenseView) => void;
  onCompanyChange: (company: string | null) => void;
  onSection: (section: FinanceSection) => void;
};

type Editing = { readonly expense: Expense | null };

type ExpenseCommand =
  | { readonly kind: 'save'; readonly expense: Expense | null; readonly body: ExpenseBody }
  | { readonly kind: 'archive' | 'restore'; readonly expense: Expense };

const DONE_MESSAGE = {
  save: 'expenses.saved',
  archive: 'expenses.archived',
  restore: 'expenses.restored',
} as const;

export const ExpensesPage = ({
  api,
  view,
  company,
  onViewChange,
  onCompanyChange,
  onSection,
}: ExpensesPageProps) => {
  const { t, locale, notify, invalidate, describeError } = usePageContext();
  const canWrite = useAuth().hasRole(FINANCE_WRITE_ROLE);
  const [editing, setEditing] = useState<Editing | null>(null);
  const expenses = useExpenses(api, view, company);
  const companies = useCompanies(api);
  const { subsidiaries } = companies;
  const subsidiaryName = (id: string | null) =>
    id === null ? t('expenses.wholeShop') : (subsidiaries.find((entry) => entry.id === id)?.name ?? '—');

  const send = (command: ExpenseCommand) => {
    if (command.kind !== 'save')
      return api.post(`/admin/finance/expenses/{id}/${command.kind}`, {
        path: { id: command.expense.id },
        body: { version: command.expense.version },
      });
    if (command.expense === null) return api.post('/admin/finance/expenses', { body: command.body });
    return api.patch('/admin/finance/expenses/{id}', {
      path: { id: command.expense.id },
      body: { ...command.body, version: command.expense.version },
    });
  };
  const change = useMutation({
    mutationFn: send,
    onSuccess: async (expense, command) => {
      notify(t(DONE_MESSAGE[command.kind], { name: expense.name }));
      setEditing(null);
      await invalidate('expenses', 'finance-overview');
    },
  });
  const open = (expense: Expense | null) => {
    change.reset();
    setEditing({ expense });
  };

  const columns: readonly Column<Expense>[] = [
    { id: 'spentOn', header: t('expenses.spentOn'), cell: (row) => formatDate(row.spentOn, locale) },
    { id: 'name', header: t('expenses.name'), primary: true, cell: (row) => row.name },
    { id: 'subsidiary', header: t('expenses.subsidiary'), cell: (row) => subsidiaryName(row.subsidiaryId) },
    {
      id: 'amount',
      header: t('expenses.amount'),
      numeric: true,
      cell: (row) => formatMoney({ amount: row.amount, currency: row.currency }, locale),
    },
  ];

  return (
    <FinanceFrame
      section="expenses"
      context={t('expenses.context')}
      action={
        canWrite ? (
          <Button
            icon={<Plus size={20} aria-hidden="true" />}
            onClick={() => {
              open(null);
            }}
          >
            {t('expenses.newTitle')}
          </Button>
        ) : undefined
      }
      onSection={onSection}
    >
      <div className={styles['toolbar']}>
        <ChoiceChips
          legend={t('expenses.view')}
          hideLegend
          locale={locale}
          value={view}
          choices={EXPENSE_VIEWS.map((value) => ({ value, label: t(`expenses.view_${value}`) }))}
          onChange={onViewChange}
        />
        <CompanyFilter api={api} company={company} onCompanyChange={onCompanyChange} />
      </div>
      <KeysetTable
        pages={expenses}
        columns={columns}
        rowKey={(row) => row.id}
        onRowClick={canWrite ? open : undefined}
        emptyTitle={view === 'active' ? t('expenses.none') : t('expenses.noneArchived')}
        emptyDescription={view === 'active' ? t('expenses.noneHelp') : undefined}
      />
      {editing === null ? null : (
        <ExpenseSheet
          expense={editing.expense}
          today={localDay(new Date())}
          subsidiaries={subsidiaries}
          submitting={change.isPending}
          errorMessage={change.isError ? (describeError(change.error) ?? null) : null}
          onClose={() => {
            setEditing(null);
          }}
          onSave={(body) => {
            change.mutate({ kind: 'save', expense: editing.expense, body });
          }}
          onToggleArchive={(expense) => {
            change.mutate({ kind: expense.archived ? 'restore' : 'archive', expense });
          }}
        />
      )}
    </FinanceFrame>
  );
};
